/**
 * The marker segments at the front of a JPEG (1.180.0). Until here the walk
 * lived in `lib/gainMap.ts`; the colour profile (`lib/colourProfile.ts`)
 * needs the same walk, and one place is the rule. A JPEG begins with `SOI`
 * and then segments of marker, length and payload up to the first scan
 * (`SOS`); the picture data behind it carries no lengths.
 *
 * **The one walk** (1.187.0, review 2026-10b C3): `panorama.ts` and
 * `exif.ts` each had another.
 */

export const APP0 = 0xe0
export const APP1 = 0xe1
export const APP2 = 0xe2
export const SOS = 0xda
export const EOI = 0xd9

/** One marker segment at the front of a JPEG: marker, where it starts, its whole length. */
export interface Segment {
  marker: number
  start: number
  size: number
}

export const encode = (s: string): Uint8Array => new TextEncoder().encode(s)

/** The segments before the picture data, in order; empty for a broken head. */
export function segmentsOf(jpeg: Uint8Array, from = 0): Segment[] {
  const out: Segment[] = []
  if (jpeg[from] !== 0xff || jpeg[from + 1] !== 0xd8) return out
  let at = from + 2
  while (at + 4 <= jpeg.length) {
    if (jpeg[at] !== 0xff) return out
    const marker = jpeg[at + 1] ?? 0
    if (marker === SOS || marker === EOI) return out
    const size = ((jpeg[at + 2] ?? 0) << 8) | (jpeg[at + 3] ?? 0)
    if (size < 2 || at + 2 + size > jpeg.length) return out
    out.push({ marker, start: at, size: 2 + size })
    at += 2 + size
  }
  return out
}

/** The head of an XMP block in APP1. */
export const XMP_HEADER = 'http://ns.adobe.com/xap/1.0/\0'

/**
 * The text of the first XMP block of a JPEG, or null; it stops at the
 * picture data. One reader for the gain map and the 360° flag (1.187.0,
 * review 2026-10b C3): `panorama.ts` had its own walk beside this one.
 */
export function xmpOf(jpeg: Uint8Array, from = 0): string | null {
  const s = segmentsOf(jpeg, from).find((seg) => seg.marker === APP1 && startsWith(payload(jpeg, seg), XMP_HEADER))
  return s === undefined ? null : new TextDecoder().decode(payload(jpeg, s).subarray(XMP_HEADER.length))
}

/** The payload of a segment, behind marker and length. */
export function payload(jpeg: Uint8Array, s: Segment): Uint8Array {
  return jpeg.subarray(s.start + 4, s.start + s.size)
}

export function startsWith(bytes: Uint8Array, head: string): boolean {
  const h = encode(head)
  return bytes.length >= h.length && h.every((b, i) => bytes[i] === b)
}

/** An APP segment around a payload. */
export function segment(marker: number, body: Uint8Array): Uint8Array {
  const out = new Uint8Array(4 + body.length)
  out.set([0xff, marker, (body.length + 2) >> 8, (body.length + 2) & 0xff])
  out.set(body, 4)
  return out
}

/** Where the leading APP0/APP1 segments (JFIF, EXIF) end: the place for a new APP segment. */
export function behindLeadingApps(jpeg: Uint8Array): number {
  let at = 2
  for (const s of segmentsOf(jpeg)) {
    if (s.marker !== APP0 && s.marker !== APP1) break
    at = s.start + s.size
  }
  return at
}

export function join(parts: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(new ArrayBuffer(parts.reduce((n, p) => n + p.length, 0)))
  let at = 0
  for (const p of parts) {
    out.set(p, at)
    at += p.length
  }
  return out
}
